import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { ContextualAnalyticsData } from '@jira/navigation-apps__sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { SidebarReady } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/metrics/index.tsx';
import { ProjectActions } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-common/src/ui/project-actions/ProjectActions.tsx';
import { ProjectExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-common/src/ui/project-expandable-menu-item/ProjectExpandableMenuItem.tsx';
import type { JcsProjectMenu$key } from './__generated__/JcsProjectMenu.graphql';
import { AsyncJcsProjectMenuContent } from './AsyncJcsProjectMenuContent.tsx';

const analyticsAttributes = {
	navState: 'v4',
};

export type JcsProjectMenuProps = {
	projectData: JcsProjectMenu$key;
	menuItemButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const JcsProjectMenu = ({
	projectData,
	menuItemButtonRef,
}: JcsProjectMenuProps): React.JSX.Element => {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const project = useFragment<JcsProjectMenu$key>(
		graphql`
			fragment JcsProjectMenu on JiraProject {
				...ProjectExpandableMenuItem
				...AsyncJcsProjectMenuContent_jiraProject
				...ProjectActions
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		projectData,
	);

	// `<ProjectActions>` is otherwise re-created on every render, giving the
	// `actionsOnHover` prop a new identity and re-rendering the menu-item trigger
	// subtree unnecessarily (measured via Chippy). `project` is a stable Relay
	// fragment reference, so memoizing keeps the element identity stable across
	// parent re-renders. Gated behind `navx-4728-jira-stability-fixes`.
	const memoizedActionsOnHover = useMemo(
		() => <ProjectActions projectFragment={project} />,
		[project],
	);
	const actionsOnHover = fg('navx-4728-jira-stability-fixes') ? (
		memoizedActionsOnHover
	) : (
		<ProjectActions projectFragment={project} />
	);

	return (
		<>
			<ContextualAnalyticsData menuId="project/service-desk" attributes={analyticsAttributes}>
				<ProjectExpandableMenuItem
					projectData={project}
					actionsOnHover={actionsOnHover}
					menuItemButtonRef={menuItemButtonRef}
				>
					<AsyncJcsProjectMenuContent projectFragment={project} />
				</ProjectExpandableMenuItem>
			</ContextualAnalyticsData>
			<SidebarReady />
		</>
	);
};
